// src/components/AdminManager.jsx
//
// Internal admin management (the app's own admin/superadmin accounts,
// a separate DB table from client organizations). Split out of
// ClientManager.jsx, where it used to live as a superadmin-only tab -
// moved to its own route to reduce confusion between "managing client
// organizations" and "managing our own internal admin accounts" (per
// request). Route-gated to superadmins only in App.jsx, same pattern
// already used for /user-manager's admin-only check.

import React, { useState, useEffect, useRef } from 'react';
import { apiFetchJSON } from '../api/apiClient';
import { FieldError, getConflictField, invalidInputStyle } from './fieldErrors';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import '../styles/ClientManager.css'; // reuses .admin-manager / .cmc-* / card styles as-is
import '../styles/fleet-theme.css';

function parseUTCTimeString(timestamp) {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'number') return new Date(timestamp * 1000);
    if (timestamp.includes('Z') || timestamp.includes('+')) return new Date(timestamp);
    try {
        return new Date(timestamp.replace(' ', 'T') + 'Z');
    } catch {
        return null;
    }
}

function AdminManager({ user, onLogout }) {
    const currentUsername = user?.username || null;

    const formatLoginTime = (alertTime) => {
        const date = parseUTCTimeString(alertTime);
        if (!date || isNaN(date.getTime())) return 'N/A';
        return new Intl.DateTimeFormat('en-GB', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        }).format(date);
    };
    const [admins, setAdmins] = useState([]);
    const [loadingAdmins, setLoadingAdmins] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ username: '', email: '', full_name: '', role: 'admin' });
    const [localMsg, setLocalMsg] = useState({ text: '', type: '' });
    // Errors shown under a form field, e.g. { email: 'This email is already used...' }
    const [fieldErrors, setFieldErrors] = useState({});
    const clearFieldError = (field) =>
        setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showMobileAddAdmin, setShowMobileAddAdmin] = useState(false);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const flash = (text, type = 'success') => {
        setLocalMsg({ text, type });
        setTimeout(() => setLocalMsg({ text: '', type: '' }), 5000);
    };

    const loadAdmins = async () => {
        setLoadingAdmins(true);
        try {
            const res = await apiFetchJSON('/api/admin/admin-management/admins');
            setAdmins(res.admins || []);
        } catch (e) {
            flash('Failed to load admins', 'error');
        } finally {
            setLoadingAdmins(false);
        }
    };

    useEffect(() => { loadAdmins(); }, []);

    const handleAddAdmin = async () => {
        if (!form.username.trim() || !form.email.trim()) {
            return flash('Username and email are required', 'error');
        }
        setSubmitting(true);
        try {
            const res = await apiFetchJSON('/api/admin/admin-management/add-admin', {
                method: 'POST',
                body: JSON.stringify(form),
            });
            if (res.success) {
                flash(
                    res.email_sent
                        ? `Admin created. Setup email sent to ${form.email}`
                        : 'Admin created, but the setup email could not be sent.',
                    res.email_sent ? 'success' : 'warning'
                );
                setForm({ username: '', email: '', full_name: '', role: 'admin' });
                setFieldErrors({});
                loadAdmins();
            } else {
                flash(res.error || 'Failed to create admin', 'error');
            }
        } catch (e) {
            const errorMessage = e?.message || 'Failed to create admin';
            const field = getConflictField(e);
            if (field) {
                setFieldErrors({ [field]: errorMessage });
            } else {
                flash(errorMessage, 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSendReset = async (admin) => {
        try {
            const res = await apiFetchJSON('/api/admin/admin-management/send-admin-reset', {
                method: 'POST',
                body: JSON.stringify({ admin_id: admin.admin_id }),
            });
            flash(res.message || (res.success ? 'Reset email sent' : 'Failed to send reset'),
                  res.success ? 'success' : 'error');
        } catch (e) {
            flash('Failed to send reset email', 'error');
        }
    };

    const handleToggle = async (admin) => {
        if (!admin.is_verified) {
            flash('Admin must verify email first', 'warning');
            return;
        }
        if (currentUsername && admin.username === currentUsername && admin.is_active) {
            flash('You cannot deactivate your own account', 'error');
            return;
        }
        try {
            const res = await apiFetchJSON(
                `/api/admin/admin-management/admins/${admin.admin_id}/status`,
                { method: 'PUT', body: JSON.stringify({ is_active: !admin.is_active }) }
            );
            if (res.error) flash(res.error, 'error');
            else loadAdmins();
        } catch (e) {
            flash('Failed to update status', 'error');
        }
    };

    // Per-account push switch. Tokens stay registered on the device; this
    // only decides whether this admin's devices are chosen when alerts fire.
    const handleTogglePush = async (admin) => {
        const enable = !admin.push_enabled;
        if (!enable && !window.confirm(
            `Turn off push notifications for ${admin.username}? All of their devices will stop receiving alerts until turned back on.`
        )) return;
        try {
            const res = await apiFetchJSON(
                `/api/admin/admin-management/admins/${admin.admin_id}/push`,
                { method: 'PUT', body: JSON.stringify({ push_enabled: enable }) }
            );
            if (res.error) flash(res.error, 'error');
            else {
                flash(`Push notifications ${enable ? 'enabled' : 'disabled'} for ${admin.username}`);
                loadAdmins();
            }
        } catch (e) {
            flash('Failed to update push notifications', 'error');
        }
    };

    // Lost phone: turn off this admin's authenticator app. They go back to
    // email login codes and can set the app up again from their Security page.
    const handleReset2fa = async (admin) => {
        if (!window.confirm(
            `Reset 2FA for ${admin.username}? Their authenticator app and backup codes will stop working and login codes will go to their email instead.`
        )) return;
        try {
            const res = await apiFetchJSON(
                `/api/admin/admin-management/admins/${admin.admin_id}/reset-2fa`,
                { method: 'POST' }
            );
            if (res.error) flash(res.error, 'error');
            else {
                flash(`2FA reset for ${admin.username}`);
                loadAdmins();
            }
        } catch (e) {
            flash(e.message || 'Failed to reset 2FA', 'error');
        }
    };

    const handleRoleChange = async (admin) => {
        if (currentUsername && admin.username === currentUsername && admin.role === 'superadmin') {
            flash('You cannot demote your own account', 'error');
            return;
        }
        const newRole = admin.role === 'superadmin' ? 'admin' : 'superadmin';
        const verb = newRole === 'superadmin' ? 'Promote to Superadmin' : 'Demote to Admin';
        if (!window.confirm(`${verb}: "${admin.username}"?`)) return;
        try {
            const res = await apiFetchJSON(
                `/api/admin/admin-management/admins/${admin.admin_id}/role`,
                { method: 'PUT', body: JSON.stringify({ role: newRole }) }
            );
            if (res.error) flash(res.error, 'error');
            else { flash(`Role updated to ${newRole}`, 'success'); loadAdmins(); }
        } catch (e) {
            flash('Failed to update role', 'error');
        }
    };

    const handleDelete = async (admin) => {
        if (currentUsername && admin.username === currentUsername) {
            flash('You cannot delete your own account', 'error');
            return;
        }
        if (!window.confirm(`Delete admin "${admin.username}"? This cannot be undone.`)) return;
        try {
            const res = await apiFetchJSON(
                `/api/admin/admin-management/admins/${admin.admin_id}`,
                { method: 'DELETE' }
            );
            if (res.error) flash(res.error, 'error');
            else { flash('Admin deleted', 'success'); loadAdmins(); }
        } catch (e) {
            flash('Failed to delete admin', 'error');
        }
    };

    // ---- Guided tour ("?" in FleetHeader) ---------------------------------
    // Example admin: someone other than yourself, since your own row has
    // DEMOTE / DEACTIVATE / DELETE disabled.
    const tourAdmin =
        admins.find((a) => !currentUsername || a.username !== currentUsername) || admins[0] || null;
    const mobileAddBeforeTourRef = useRef(false);
    usePageTour('admin-manager', {
        isMobile,
        admins,
        tourAdmin,
        currentUsername,
        openMobileAddPanel: () => setShowMobileAddAdmin(true),
        closeMobileAddPanel: () => setShowMobileAddAdmin(false),
        onTourStart: () => { mobileAddBeforeTourRef.current = showMobileAddAdmin; },
        onTourEnd: () => setShowMobileAddAdmin(mobileAddBeforeTourRef.current),
    }, { ready: !loadingAdmins });

    // 'warning' has no style of its own in fleet-theme - shown as an error, as before.
    const isErrorMsg = localMsg.type === 'error' || localMsg.type === 'warning';

    const content = (
            <div className="admin-manager">
            {localMsg.text && (
                <div className={`ft-toolbar-${isErrorMsg ? 'error' : 'success'}`} role="alert">
                    <i className={`fas fa-${isErrorMsg ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
                    {localMsg.text}
                    <button type="button" className="btn-close" onClick={() => setLocalMsg({ text: '', type: '' })}></button>
                </div>
            )}

            {/* Add Admin - flat inline bar, no card chrome, matching mockup 3d */}
            <div className="cm-add-client-bar" data-tour="am-add">
                {isMobile && (
                    <button
                        className="cm-mobile-toggle"
                        data-tour="am-add-toggle"
                        onClick={() => setShowMobileAddAdmin((s) => !s)}
                    >
                        {showMobileAddAdmin ? 'HIDE' : '+ ADD ADMIN'}
                    </button>
                )}
                {(!isMobile) && (
                    <div className="cm-add-form-grid">
                        <div className="cm-field">
                            <label htmlFor="adminUsername">USERNAME *</label>
                            <input
                                id="adminUsername"
                                type="text"
                                placeholder="username"
                                value={form.username}
                                onChange={(e) => { clearFieldError('username'); setForm({ ...form, username: e.target.value }); }}
                                style={fieldErrors.username ? invalidInputStyle : undefined}
                                aria-invalid={!!fieldErrors.username}
                                className="cm-input"
                                required
                            />
                            <FieldError message={fieldErrors.username} />
                        </div>
                        <div className="cm-field">
                            <label htmlFor="adminEmailNew">EMAIL *</label>
                            <input
                                id="adminEmailNew"
                                type="email"
                                placeholder="admin@example.com"
                                value={form.email}
                                onChange={(e) => { clearFieldError('email'); setForm({ ...form, email: e.target.value }); }}
                                style={fieldErrors.email ? invalidInputStyle : undefined}
                                aria-invalid={!!fieldErrors.email}
                                className="cm-input"
                                required
                            />
                            <FieldError message={fieldErrors.email} />
                        </div>
                        <div className="cm-field" style={isMobile ? undefined : { flex: '0 0 160px' }}>
                            <label htmlFor="adminRole">ROLE</label>
                            <select
                                id="adminRole"
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                className="cm-input"
                            >
                                <option value="admin">Admin</option>
                                <option value="superadmin">Superadmin</option>
                            </select>
                        </div>
                        <button
                            className="cm-add-btn"
                            onClick={handleAddAdmin}
                            disabled={submitting}
                        >
                            {submitting ? 'CREATING...' : '+ CREATE ADMIN'}
                        </button>
                    </div>
                )}
                {(isMobile && showMobileAddAdmin) && (
                    <div className="cm-add-form-grid" data-tour="am-add-form">
                        <div className="cm-field-row">
                            <div className="cm-field">
                                <label htmlFor="adminUsername">USERNAME *</label>
                                <input
                                    id="adminUsername"
                                    type="text"
                                    placeholder="username"
                                    value={form.username}
                                    onChange={(e) => { clearFieldError('username'); setForm({ ...form, username: e.target.value }); }}
                                    style={fieldErrors.username ? invalidInputStyle : undefined}
                                    aria-invalid={!!fieldErrors.username}
                                    className="cm-input"
                                    required
                                />
                                <FieldError message={fieldErrors.username} />
                            </div>
                            <div className="cm-field" style={isMobile ? undefined : { flex: '0 0 160px' }}>
                                <label htmlFor="adminRole">ROLE</label>
                                <select
                                    id="adminRole"
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    className="cm-input"
                                >
                                    <option value="admin">Admin</option>
                                    <option value="superadmin">Superadmin</option>
                                </select>
                            </div>
                        </div>

                        <div className="cm-field-row">
                            <div className="cm-field">
                                <label htmlFor="adminEmailNew">EMAIL *</label>
                                <input
                                    id="adminEmailNew"
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={form.email}
                                    onChange={(e) => { clearFieldError('email'); setForm({ ...form, email: e.target.value }); }}
                                    style={fieldErrors.email ? invalidInputStyle : undefined}
                                    aria-invalid={!!fieldErrors.email}
                                    className="cm-input"
                                    required
                                />
                                <FieldError message={fieldErrors.email} />
                            </div>
                            <button
                                className="cm-add-btn"
                                onClick={handleAddAdmin}
                                disabled={submitting}
                            >
                                {submitting ? 'CREATING...' : '+ CREATE ADMIN'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Admins List */}
            <div className="cm-panel">
                <div className="cm-tabs-row" data-tour="am-head">
                    <span className="cm-tab cm-active">INTERNAL ADMINS {admins.length}</span>
                </div>
                <div className="cm-panel-body">
                    {loadingAdmins ? (
                        <p className="cm-loading-text">Loading admins...</p>
                    ) : admins.length === 0 ? (
                        <p className="text-muted">No internal admins yet.</p>
                    ) : isMobile ? (
                        <div className="cmc-user-list">
                            {admins.map((a) => (
                                <div key={a.admin_id} className="cmc-user-row" data-tour={tourAdmin && a.admin_id === tourAdmin.admin_id ? 'am-admin' : undefined}>
                                    <div className="cmc-user-info">
                                        <div className="cmc-user-name">{a.username}</div>
                                        <div className="cmc-user-meta">
                                            <span className={`cmc-user-role cmc-user-role-${a.role === 'superadmin' ? 'admin' : 'operator'}`}>
                                                {a.role === 'superadmin' ? 'SUPERADMIN' : 'ADMIN'}
                                            </span>
                                            {a.email || '-'}
                                        </div>
                                        <div className="cmc-user-status">
                                            <span className={`cmc-user-role cmc-user-role-${!a.is_verified ? 'viewer' : a.is_active ? 'operator' : 'admin'}`}>
                                                {!a.is_verified ? 'PENDING' : a.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                </span>
                                            {' · '}
                                            {a.last_login ? `Last login: ${formatLoginTime ? formatLoginTime(a.last_login) : a.last_login}` : 'Never logged in'}
                                        </div>
                                    </div>
                                    <div className="cmc-user-actions" data-tour={tourAdmin && a.admin_id === tourAdmin.admin_id ? 'am-admin-actions' : undefined}>
                                        <button
                                            className="cmc-action-label"
                                            onClick={() => handleSendReset(a)}
                                            title="Send Password Reset Email"
                                        >
                                            RESET PW
                                        </button>
                                        <button
                                            className="cmc-action-label"
                                            onClick={() => handleRoleChange(a)}
                                            title={a.role === 'superadmin' ? 'Demote to Admin' : 'Promote to Superadmin'}
                                            disabled={currentUsername && a.username === currentUsername}
                                        >
                                            {a.role === 'superadmin' ? 'DEMOTE' : 'PROMOTE'}
                                        </button>
                                        <button
                                            className={'cmc-action-label cmc-action-danger' + (a.push_enabled ? '' : ' cmc-action-muted')}
                                            onClick={() => handleTogglePush(a)}
                                            title={a.push_enabled ? 'Push notifications are on - tap to turn off' : 'Push notifications are off - tap to turn on'}
                                        >
                                            {a.push_enabled ? 'NOTIF OFF' : 'NOTIF ON'}
                                        </button>
                                        {a.totp_enabled && a.username !== user?.username && (
                                            <button
                                                className="cmc-action-label"
                                                onClick={() => handleReset2fa(a)}
                                                title="Authenticator app is on - reset it if they lost their phone"
                                            >
                                                RESET 2FA
                                            </button>
                                        )}
                                        <button
                                            className="cmc-action-label cmc-action-muted"
                                            onClick={() => handleToggle(a)}
                                            title={!a.is_verified ? 'Must verify email first' : (a.is_active ? 'Deactivate' : 'Activate')}
                                            disabled={!a.is_verified || (currentUsername && a.username === currentUsername)}
                                        >
                                            {!a.is_verified ? 'PENDING' : (a.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                        </button>
                                        <button
                                            className="cmc-action-label cmc-action-danger"
                                            onClick={() => handleDelete(a)}
                                            title={currentUsername && a.username === currentUsername ? 'You cannot delete your own account' : 'Delete Admin'}
                                            disabled={currentUsername && a.username === currentUsername}
                                        >
                                            DELETE
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="cm-admin-grid-wrap">
                            <div className="cm-admin-grid-header">
                                <div>USERNAME</div>
                                <div>EMAIL</div>
                                <div>ROLE</div>
                                <div>STATUS</div>
                                <div>LAST LOGIN</div>
                                <div className="cm-num">ACTIONS</div>
                            </div>
                            {admins.map((a, idx) => (
                                <div key={a.admin_id} className={'cm-admin-grid-row' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '')} data-tour={tourAdmin && a.admin_id === tourAdmin.admin_id ? 'am-admin' : undefined}>
                                    <div className="cm-client-name">{a.username}</div>
                                    <div className="cm-email">{a.email || '-'}</div>
                                    <div>
                                        <span className={'cm-role-chip ' + (a.role === 'superadmin' ? 'cm-admin' : 'cm-operator')}>
                                            {a.role === 'superadmin' ? 'SUPERADMIN' : 'ADMIN'}
                                        </span>
                                    </div>
                                    <div>
                                        {!a.is_verified ? (
                                            <span className="cm-status-chip cm-pending">PENDING</span>
                                        ) : a.is_active ? (
                                            <span className="cm-status-chip cm-active">ACTIVE</span>
                                        ) : (
                                            <span className="cm-status-chip cm-inactive">INACTIVE</span>
                                        )}
                                    </div>
                                    <div className="cm-created">
                                        {a.last_login ?
                                            (formatLoginTime ? formatLoginTime(a.last_login) : a.last_login) :
                                            'Never'
                                        }
                                    </div>
                                    <div className="cm-row-actions" data-tour={tourAdmin && a.admin_id === tourAdmin.admin_id ? 'am-admin-actions' : undefined}>
                                        <button
                                            className="cm-action"
                                            onClick={() => handleSendReset(a)}
                                            title="Send Password Reset Email"
                                        >
                                            RESET PW
                                        </button>
                                        <button
                                            className="cm-action"
                                            onClick={() => handleRoleChange(a)}
                                            title={
                                                a.role === 'superadmin'
                                                    ? 'Demote to Admin'
                                                    : 'Promote to Superadmin'
                                            }
                                            disabled={currentUsername && a.username === currentUsername}
                                        >
                                            {a.role === 'superadmin' ? 'DEMOTE' : 'PROMOTE'}
                                        </button>
                                        <button
                                            className={'cm-action cm-action-danger' + (a.push_enabled ? '' : ' cm-action-muted')}
                                            onClick={() => handleTogglePush(a)}
                                            title={a.push_enabled ? 'Push notifications are on - click to turn off' : 'Push notifications are off - click to turn on'}
                                        >
                                            {a.push_enabled ? 'NOTIF OFF' : 'NOTIF ON'}
                                        </button>
                                        {a.totp_enabled && a.username !== user?.username && (
                                            <button
                                                className="cm-action"
                                                onClick={() => handleReset2fa(a)}
                                                title="Authenticator app is on - reset it if they lost their phone"
                                            >
                                                RESET 2FA
                                            </button>
                                        )}
                                        <button
                                            className="cm-action cm-action-muted"
                                            onClick={() => handleToggle(a)}
                                            title={
                                                !a.is_verified
                                                    ? 'Must verify email first'
                                                    : (a.is_active ? 'Deactivate' : 'Activate')
                                            }
                                            disabled={!a.is_verified || (currentUsername && a.username === currentUsername)}
                                        >
                                            {!a.is_verified ? 'PENDING' : (a.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                        </button>
                                        <button
                                            className="cm-action cm-action-danger"
                                            onClick={() => handleDelete(a)}
                                            title={
                                                currentUsername && a.username === currentUsername
                                                    ? 'You cannot delete your own account'
                                                    : 'Delete Admin'
                                            }
                                            disabled={currentUsername && a.username === currentUsername}
                                        >
                                            DELETE
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="App cm-client-manager">
            <FleetHeader title="Admin Manager" user={user} onLogout={onLogout} />
            <div className="cm-dashboard-container client-manager">
                {content}
            </div>
        </div>
    );
}

export default AdminManager;
